/* eslint-disable class-methods-use-this */
import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import { _ } from '@splunk/ui-utils/i18n';
import Provider from '@splunk/visualization-context/Provider';

import {
    ActiveDirectory,
    Check,
    Cloud,
    Datacenter,
    Datacenters,
    Datastore,
    Datastores,
    Desktop,
    Firewall,
    Gear,
    Laptop,
    LoadBalancer,
    Mobile,
    Profile,
    Server,
    Users,
} from '@splunk/dashboard-icons';
import { b64EncodeUnicode } from '@splunk/dashboard-utils';

export const toDataURI = (Svg: React.FC): string =>
    `data:image/svg+xml;base64,${b64EncodeUnicode(
        renderToStaticMarkup(<Svg />)
    )}`;

interface Icon {
    dataURI: string;
    metaData: Record<string, string>;
}

type IconMap = Record<string, Icon>;

interface IconListItem {
    id: string;
    metaData?: Record<string, string>;
}

export const defaultIconList: IconMap = {
    activeDirectory: {
        dataURI: toDataURI(ActiveDirectory),
        metaData: {
            name: 'Active Directory',
        },
    },
    check: {
        dataURI: toDataURI(Check),
        metaData: {
            name: 'Check',
        },
    },
    cloud: {
        dataURI: toDataURI(Cloud),
        metaData: {
            name: 'Cloud',
        },
    },
    datacenter: {
        dataURI: toDataURI(Datacenter),
        metaData: {
            name: 'Datacenter',
        },
    },
    datacenters: {
        dataURI: toDataURI(Datacenters),
        metaData: {
            name: 'Datacenters',
        },
    },
    datastore: {
        dataURI: toDataURI(Datastore),
        metaData: {
            name: 'Datastore',
        },
    },
    datastores: {
        dataURI: toDataURI(Datastores),
        metaData: {
            name: 'Datastores',
        },
    },
    desktop: {
        dataURI: toDataURI(Desktop),
        metaData: {
            name: 'Desktop',
        },
    },
    firewall: {
        dataURI: toDataURI(Firewall),
        metaData: {
            name: 'Firewall',
        },
    },
    gear: {
        dataURI: toDataURI(Gear),
        metaData: {
            name: 'Gear',
        },
    },
    laptop: {
        dataURI: toDataURI(Laptop),
        metaData: {
            name: 'Laptop',
        },
    },
    loadBalancer: {
        dataURI: toDataURI(LoadBalancer),
        metaData: {
            name: 'Load Balancer',
        },
    },
    mobile: {
        dataURI: toDataURI(Mobile),
        metaData: {
            name: 'Mobile',
        },
    },
    profile: {
        dataURI: toDataURI(Profile),
        metaData: {
            name: 'Profile',
        },
    },
    server: {
        dataURI: toDataURI(Server),
        metaData: {
            name: 'Server',
        },
    },
    users: {
        dataURI: toDataURI(Users),
        metaData: {
            name: 'Users',
        },
    },
};

const isSearched = (
    search: string | null,
    iconMeta: Record<string, string>
): boolean => {
    return (
        !!search &&
        Object.keys(iconMeta).some((attr) => {
            const iconMetaValue = iconMeta[attr].toLowerCase();
            return iconMetaValue.includes(search.toLowerCase());
        })
    );
};

const createIcon = (
    id: string,
    iconMeta: Record<string, string>,
    meta?: string[]
): IconListItem => {
    const icon: IconListItem = { id };
    if (meta?.length) {
        meta.forEach((attr) => {
            if (iconMeta[attr]) {
                icon.metaData ??= {};
                icon.metaData[attr] = iconMeta[attr];
            }
        });
    }

    return icon;
};

/**
 * a local icon provider
 */
export class StandardIconProvider extends Provider {
    iconList: IconMap;

    constructor({ iconList }: { iconList?: IconMap } = {}) {
        super();

        this.iconList = {
            ...iconList,
            ...defaultIconList,
        };
    }

    /**
     * return provider type
     * @return {String} 'splunk-default'
     */
    getType(): string {
        return 'splunk-default';
    }

    /**
     * upload one new icon
     */
    async upload(): Promise<void> {
        throw new Error(
            _('Can not upload icons to the standard icon provider.')
        );
    }

    /**
     * get one icon by icon id
     */
    async getById(id: string): Promise<Icon> {
        if (this.iconList[id]) {
            return this.iconList[id];
        }
        throw new Error(_('The icon is not found.'));
    }

    /**
     * get all icon ids
     * @params options
     * @params options.meta   a list of meta data attribute that would be returned with each id
     * @params options.search  a search string to filter the returned icons
     */
    async listIds({
        offset = 0,
        count = 100,
        meta = [],
        search = null,
    }: {
        offset?: number;
        count?: number;
        meta?: string[];
        search?: string | null;
    } = {}): Promise<IconListItem[]> {
        try {
            const icons = this.iconList;
            return Object.keys(icons)
                .sort((a, b) =>
                    a.localeCompare(b, undefined, { sensitivity: 'base' })
                )
                .filter((id) => {
                    const iconMeta = icons[id].metaData ?? {};
                    return !search || isSearched(search, iconMeta);
                })
                .slice(offset, offset + count)
                .map((id) => createIcon(id, icons[id].metaData ?? {}, meta));
        } catch {
            throw new Error(_('The icon ids cannot be listed.'));
        }
    }

    /**
     * delete an icon by icon id
     */
    async deleteById(): Promise<void> {
        throw new Error(_('The icon cannot be deleted.'));
    }
}
